import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getAllTeamMembers } from "../services/azureTeamService";
import { getTimekeepingData } from "../services/azureTimekeepingService";
import {
  getHrSettings,
  getCorrections,
  calculateGleitzeit,
  calculateWorkingDays,
  buildPensumPeriods,
  getPensumAtDate,
} from "../services/azureHrService";
import { getSalaryEntries } from "../services/azureSalaryService";
import { hasRole, ROLES } from "../utils/permissions";
import { useTheme } from "../contexts/ThemeContext";

const CURRENT_YEAR = new Date().getFullYear();
const AVAILABLE_YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1];
const ArG_WEEK_LIMIT = 45;
const KIGA_JAHRES_LIMIT = 170;

// ----- Ferien helpers (KPI cards) ---------------------------------------

const isFerienHourly = (entry) =>
  (entry.typeName || "").toLowerCase().includes("ferien in h");

const isFerienFullDay = (entry) => {
  const t = (entry.typeName || "").toLowerCase();
  if (isFerienHourly(entry)) return false;
  return (
    entry.isFullDay &&
    (t.includes("ferien") || t.includes("urlaub") || t.includes("vacation"))
  );
};

const sumHolidayDays = (entries, tagesarbeitszeit) => {
  const fullDayCount = entries.filter(isFerienFullDay).length;
  const hourlyHours = entries
    .filter(isFerienHourly)
    .reduce((s, e) => s + (e.durationHours || 0) + (e.durationMinutes || 0) / 60, 0);
  return fullDayCount + hourlyHours / (tagesarbeitszeit || 8.24);
};

// Simpler Ferien detection per US-TEAM-004 (1 Eintrag = 1 Tag)
const isFerienSimple = (entry) => {
  const t = (entry.typeName || "").toLowerCase();
  return t.includes("ferien") || t.includes("urlaub") || t.includes("vacation");
};

const entryHours = (entry) =>
  (entry.durationHours || 0) + (entry.durationMinutes || 0) / 60;

const formatDays = (value) => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (!Number.isFinite(num)) return "0";
  return Number.isInteger(num) ? String(num) : num.toFixed(1);
};

const formatHours = (hours) => {
  if (!Number.isFinite(hours)) return "0.0";
  return hours.toFixed(1);
};

// ----- ISO week helpers -------------------------------------------------

const pad2 = (n) => String(n).padStart(2, "0");

const getISOWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), week: weekNum };
};

// Monday of the given ISO week, as YYYY-MM-DD in UTC
const getMondayOfISOWeek = (year, week) => {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = jan4.getUTCDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - jan4Dow + 1);
  const monday = new Date(week1Monday);
  monday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  return monday;
};

const toISOStr = (utcDate) =>
  `${utcDate.getUTCFullYear()}-${pad2(utcDate.getUTCMonth() + 1)}-${pad2(utcDate.getUTCDate())}`;

const formatMonFri = (monday) => {
  const friday = new Date(monday);
  friday.setUTCDate(monday.getUTCDate() + 4);
  return `${pad2(monday.getUTCDate())}.${pad2(monday.getUTCMonth() + 1)}.–${pad2(friday.getUTCDate())}.${pad2(friday.getUTCMonth() + 1)}.`;
};

// ----- Quarter helpers --------------------------------------------------

const getQuarterFromDate = (dateStr) => {
  if (!dateStr) return 0;
  const month = parseInt(dateStr.substring(5, 7), 10);
  return Math.ceil(month / 3);
};

const getQuarterRange = (year, q) => {
  const startMonth = (q - 1) * 3;
  const endMonth = startMonth + 2;
  const start = `${year}-${pad2(startMonth + 1)}-01`;
  const lastDay = new Date(year, endMonth + 1, 0).getDate();
  const end = `${year}-${pad2(endMonth + 1)}-${pad2(lastDay)}`;
  return { start, end };
};

// Normalize feiertage to an array of ISO date strings
const normalizeFeiertage = (feiertage) =>
  (feiertage || []).map((f) => (typeof f === "string" ? f : f.date));

// ----- Project grouping helpers (US-TEAM-005) ---------------------------

const getEntryCategory = (entry) => {
  if (entry.code && entry.code.length >= 2) {
    return {
      type: "project",
      key: entry.code,
      name: entry.contractNoteName || entry.code,
    };
  }
  const category = entry.typeName || entry.contractNoteName || "Sonstiges";
  return { type: "internal", key: category, name: category };
};

const aggregateEntries = (entries) => {
  let totalMinutes = 0;
  entries.forEach((entry) => {
    totalMinutes += (entry.durationHours || 0) * 60 + (entry.durationMinutes || 0);
  });
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
    decimal: totalMinutes / 60,
    count: entries.length,
  };
};

const groupEntriesByProject = (entries) => {
  const projects = {};
  const internal = {};
  entries.forEach((entry) => {
    const cat = getEntryCategory(entry);
    const bucket = cat.type === "project" ? projects : internal;
    if (!bucket[cat.key]) bucket[cat.key] = { name: cat.name, entries: [] };
    bucket[cat.key].entries.push(entry);
  });

  const toList = (bucket) =>
    Object.entries(bucket)
      .map(([code, data]) => ({
        code,
        name: data.name,
        ...aggregateEntries(data.entries),
      }))
      .sort((a, b) => b.decimal - a.decimal);

  return { projectList: toList(projects), internalList: toList(internal) };
};

const TOP_PROJECTS_IN_CHART = 10;

// ----- Component --------------------------------------------------------

const TeamPersonAuswertung = ({ user }) => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { getComputedThemeValue, currentTheme } = useTheme();

  const [member, setMember] = useState(null);
  const [allMembers, setAllMembers] = useState([]);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [hrSettings, setHrSettings] = useState(null);
  const [yearEntries, setYearEntries] = useState([]);
  const [salaryEntries, setSalaryEntries] = useState([]);
  const [gleitzeitInfo, setGleitzeitInfo] = useState(null);
  const [holidaysPlanned, setHolidaysPlanned] = useState(0);
  const [totalHoursWorked, setTotalHoursWorked] = useState(0);
  const [loading, setLoading] = useState(true);
  const [memberLoading, setMemberLoading] = useState(true);
  const [error, setError] = useState(null);
  const [personMenuOpen, setPersonMenuOpen] = useState(false);
  const personMenuRef = useRef(null);

  const isAdmin = hasRole(user, ROLES.ADMIN);
  const isPL = hasRole(user, ROLES.PL);
  const canViewAnyone = isAdmin || isPL;
  const canSeeKiga = canViewAnyone;
  const ownTeamId = user?.teamMember?.teamId;

  useEffect(() => {
    if (!canViewAnyone && ownTeamId && teamId !== ownTeamId) {
      navigate(`/team/${ownTeamId}/auswertung`, { replace: true });
      return;
    }

    if (!canViewAnyone && !ownTeamId) {
      setError("Sie haben keine Berechtigung, diese Auswertung anzusehen.");
      setMemberLoading(false);
      setLoading(false);
      return;
    }

    loadMember();
  }, [teamId, canViewAnyone, ownTeamId]);

  useEffect(() => {
    if (member) {
      loadAuswertung();
    }
  }, [member, selectedYear]);

  // Personen-Dropdown schliessen bei Klick ausserhalb oder Escape
  useEffect(() => {
    if (!personMenuOpen) return;
    const handleDocClick = (e) => {
      if (personMenuRef.current && !personMenuRef.current.contains(e.target)) {
        setPersonMenuOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setPersonMenuOpen(false);
    };
    document.addEventListener("mousedown", handleDocClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleDocClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [personMenuOpen]);

  const sortedMembers = useMemo(() => {
    return [...allMembers]
      .filter((m) => m.name)
      .sort((a, b) => a.name.localeCompare(b.name, "de"));
  }, [allMembers]);

  const loadMember = async () => {
    setMemberLoading(true);
    setError(null);
    try {
      const members = await getAllTeamMembers();
      setAllMembers(members);
      const found = members.find((m) => m.teamId === teamId);
      if (!found) {
        setError("Teammitglied nicht gefunden.");
        setMember(null);
      } else {
        setMember(found);
      }
    } catch (err) {
      setError(`Fehler beim Laden der Person: ${err.message || err}`);
      setMember(null);
    } finally {
      setMemberLoading(false);
    }
  };

  const loadAuswertung = async () => {
    setLoading(true);
    setError(null);
    try {
      const [timekeeping, settings, corrections, salary] = await Promise.all([
        getTimekeepingData(),
        getHrSettings(String(selectedYear)),
        getCorrections(member.teamId, selectedYear).catch(() => []),
        getSalaryEntries(member.employeeUuid).catch(() => []),
      ]);
      setSalaryEntries(salary);

      if (!settings) {
        setGleitzeitInfo(null);
        setTotalHoursWorked(0);
        setHolidaysPlanned(0);
        setHrSettings(null);
        setYearEntries([]);
        setError(`Für ${selectedYear} sind keine HR-Einstellungen hinterlegt.`);
        return;
      }
      setHrSettings(settings);

      const memberEntries = timekeeping.filter(
        (entry) =>
          entry.employeeUuid === member.employeeUuid ||
          entry.employeeName === member.name
      );
      const entriesOfYear = memberEntries.filter(
        (entry) => entry.date && entry.date.startsWith(String(selectedYear))
      );
      setYearEntries(entriesOfYear);

      // Gleitzeit: calc since latest correction or year start
      const todayStr = new Date().toISOString().split("T")[0];
      const relevantCorrections = corrections.filter((c) => c.date <= todayStr);
      const latestCorrection =
        relevantCorrections.length > 0
          ? relevantCorrections[relevantCorrections.length - 1]
          : null;
      const calcStart = latestCorrection
        ? (() => {
            const d = new Date(latestCorrection.date);
            d.setDate(d.getDate() + 1);
            return d.toISOString().split("T")[0];
          })()
        : `${selectedYear}-01-01`;

      const entriesSinceStart = entriesOfYear.filter((e) => e.date >= calcStart);

      // Ferien aus totalHoursWorked ausschliessen — Credit erfolgt in calculateGleitzeit.
      let totalMinutes = 0;
      entriesSinceStart.forEach((entry) => {
        if (isFerienFullDay(entry) || isFerienHourly(entry)) return;
        totalMinutes += (entry.durationHours || 0) * 60 + (entry.durationMinutes || 0);
      });
      const hoursWorked = totalMinutes / 60;
      setTotalHoursWorked(hoursWorked);

      const tagesarbeitszeit = settings.tagesarbeitszeit || 8.24;
      const pastHolidayEntries = entriesSinceStart.filter((e) => e.date <= todayStr);
      const futureHolidayEntries = entriesSinceStart.filter((e) => e.date > todayStr);

      // Nur vergangene Ferien-Einträge an calculateGleitzeit übergeben (für Credit)
      const pastFerienEntries = pastHolidayEntries.filter(
        (e) => isFerienFullDay(e) || isFerienHourly(e)
      );
      const planned = sumHolidayDays(futureHolidayEntries, tagesarbeitszeit);
      setHolidaysPlanned(planned);

      const info = calculateGleitzeit(settings, member, hoursWorked, corrections, pastFerienEntries, salary);
      setGleitzeitInfo(info);
    } catch (err) {
      setError(`Fehler beim Laden der Auswertung: ${err.message || err}`);
      setGleitzeitInfo(null);
      setHrSettings(null);
      setYearEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const gleitzeitValue = useMemo(() => {
    if (!gleitzeitInfo) return 0;
    return parseFloat(gleitzeitInfo.gleitzeit);
  }, [gleitzeitInfo]);

  // ----- Pensumshistorie aus salaryEntries -------------------------------

  const pensumPeriods = useMemo(() => {
    if (!member) return [];
    const yearStart = `${selectedYear}-01-01`;
    const yearEnd = `${selectedYear}-12-31`;
    const personStart = member.start && member.start > yearStart ? member.start : yearStart;
    const personEnd = member.ende && member.ende < yearEnd ? member.ende : yearEnd;
    if (personStart > personEnd) return [];
    if (salaryEntries && salaryEntries.length > 0) {
      return buildPensumPeriods(salaryEntries, personStart, personEnd);
    }
    // Fallback: member.pensum als einzelne Periode
    return [{ from: personStart, to: personEnd, pensum: member.pensum ?? 100 }];
  }, [salaryEntries, member, selectedYear]);

  // ----- Weekly aggregation ---------------------------------------------

  const weeklyData = useMemo(() => {
    if (!hrSettings || !member || yearEntries.length === 0) return [];
    const tagesarbeitszeit = hrSettings.tagesarbeitszeit || 8.24;
    const feiertageDates = new Set(normalizeFeiertage(hrSettings.feiertage));
    const todayStr = new Date().toISOString().split("T")[0];

    const buckets = new Map();
    yearEntries.forEach((entry) => {
      if (!entry.date) return;
      const [y, m, d] = entry.date.split("-").map(Number);
      const iso = getISOWeek(new Date(y, m - 1, d));
      const key = `${iso.year}-${iso.week}`;
      if (!buckets.has(key)) {
        buckets.set(key, { isoYear: iso.year, week: iso.week, entries: [] });
      }
      buckets.get(key).entries.push(entry);
    });

    const rows = [];
    buckets.forEach(({ isoYear, week, entries }) => {
      const monday = getMondayOfISOWeek(isoYear, week);
      const mondayStr = toISOStr(monday);
      const friday = new Date(monday);
      friday.setUTCDate(monday.getUTCDate() + 4);
      const fridayStr = toISOStr(friday);

      const feiertagCount = [...feiertageDates].filter(
        (fd) => fd >= mondayStr && fd <= fridayStr
      ).length;

      // Pensum pro Woche aus der Pensumshistorie (Stichtag: Montag)
      const weekPensum = getPensumAtDate(pensumPeriods, mondayStr);
      const pensumTag = tagesarbeitszeit * weekPensum;

      const sollHours = (5 - feiertagCount) * pensumTag;
      // Ferien aus istHours ausschliessen — konsistent mit calculateGleitzeit
      const istHours = entries
        .filter((e) => !isFerienFullDay(e) && !isFerienHourly(e))
        .reduce((s, e) => s + entryHours(e), 0);

      // Ferien-Credit analog calculateGleitzeit: pro Ganztag pensumTag, pro "Ferien in h" die echten Stunden.
      // Verhindert, dass reine Ferienwochen ein negatives Delta zeigen.
      const ferienFullDayCount = entries.filter(isFerienFullDay).length;
      const ferienHourlyHours = entries
        .filter(isFerienHourly)
        .reduce((s, e) => s + entryHours(e), 0);
      const ferienCreditHours = ferienFullDayCount * pensumTag + ferienHourlyHours;
      const delta = istHours + ferienCreditHours - sollHours;

      const ferienEntries = entries.filter(isFerienSimple);
      const ferienTaken = ferienEntries.filter((e) => e.date <= todayStr).length;
      const ferienPlanned = ferienEntries.filter((e) => e.date > todayStr).length;

      const overtime = Math.max(0, istHours - ArG_WEEK_LIMIT);

      rows.push({
        key: `${isoYear}-KW${pad2(week)}`,
        label: `${isoYear}-KW${pad2(week)}`,
        dateRange: formatMonFri(monday),
        mondayStr,
        fridayStr,
        istHours,
        sollHours,
        delta,
        ferienTaken,
        ferienPlanned,
        overtime,
      });
    });

    rows.sort((a, b) => (a.key < b.key ? -1 : 1));
    return rows;
  }, [hrSettings, member, yearEntries, pensumPeriods]);

  const weeklyTotals = useMemo(() => {
    return weeklyData.reduce(
      (acc, r) => ({
        ist: acc.ist + r.istHours,
        soll: acc.soll + r.sollHours,
        delta: acc.delta + r.delta,
        ferienTaken: acc.ferienTaken + r.ferienTaken,
        ferienPlanned: acc.ferienPlanned + r.ferienPlanned,
        overtime: acc.overtime + r.overtime,
      }),
      { ist: 0, soll: 0, delta: 0, ferienTaken: 0, ferienPlanned: 0, overtime: 0 }
    );
  }, [weeklyData]);

  // ----- Quarterly aggregation ------------------------------------------

  const quarterlyData = useMemo(() => {
    if (!hrSettings || !member) return [];
    const tagesarbeitszeit = hrSettings.tagesarbeitszeit || 8.24;
    const feiertage = hrSettings.feiertage || [];
    const todayStr = new Date().toISOString().split("T")[0];

    return [1, 2, 3, 4].map((q) => {
      const { start, end } = getQuarterRange(selectedYear, q);
      const workingDays = calculateWorkingDays(start, end, feiertage, []);
      // Pensum pro Quartal aus der Pensumshistorie (Stichtag: Quartalsbeginn)
      const quarterPensum = getPensumAtDate(pensumPeriods, start);
      const pensumTag = tagesarbeitszeit * quarterPensum;
      const sollHours = workingDays * pensumTag;

      const qEntries = yearEntries.filter((e) => getQuarterFromDate(e.date) === q);
      // Ferien aus istHours ausschliessen — konsistent mit calculateGleitzeit
      const istHours = qEntries
        .filter((e) => !isFerienFullDay(e) && !isFerienHourly(e))
        .reduce((s, e) => s + entryHours(e), 0);

      // Ferien-Credit: pro Ganztag pensumTag, pro "Ferien in h" die echten Stunden.
      // Delta = Ist + Credit − Soll, konsistent mit Gleitzeit-Berechnung.
      const ferienFullDayCount = qEntries.filter(isFerienFullDay).length;
      const ferienHourlyHours = qEntries
        .filter(isFerienHourly)
        .reduce((s, e) => s + entryHours(e), 0);
      const ferienCreditHours = ferienFullDayCount * pensumTag + ferienHourlyHours;
      const delta = istHours + ferienCreditHours - sollHours;

      const ferienTaken = qEntries.filter(
        (e) => isFerienSimple(e) && e.date <= todayStr
      ).length;

      return {
        key: `Q${q}`,
        label: `Q${q}`,
        istHours,
        sollHours,
        delta,
        ferienTaken,
      };
    });
  }, [hrSettings, member, yearEntries, selectedYear, pensumPeriods]);

  const quarterlyTotals = useMemo(() => {
    return quarterlyData.reduce(
      (acc, r) => ({
        ist: acc.ist + r.istHours,
        soll: acc.soll + r.sollHours,
        delta: acc.delta + r.delta,
        ferienTaken: acc.ferienTaken + r.ferienTaken,
      }),
      { ist: 0, soll: 0, delta: 0, ferienTaken: 0 }
    );
  }, [quarterlyData]);

  // ----- KIGA overtime summary ------------------------------------------

  const overtimeWeeks = useMemo(
    () => weeklyData.filter((w) => w.overtime > 0),
    [weeklyData]
  );

  const kigaAccumulated = useMemo(
    () => overtimeWeeks.reduce((s, w) => s + w.overtime, 0),
    [overtimeWeeks]
  );

  const kigaRemaining = Math.max(0, KIGA_JAHRES_LIMIT - kigaAccumulated);
  const kigaPercent = (kigaAccumulated / KIGA_JAHRES_LIMIT) * 100;
  const kigaStatus =
    kigaPercent >= 100 ? "error" : kigaPercent >= 80 ? "warning" : "normal";

  // ----- Project grouping (US-TEAM-005) --------------------------------

  const { projectList, internalList } = useMemo(
    () => groupEntriesByProject(yearEntries),
    [yearEntries]
  );

  const projectTotals = useMemo(() => {
    const projectHours = projectList.reduce((s, p) => s + p.decimal, 0);
    const internalHours = internalList.reduce((s, p) => s + p.decimal, 0);
    return {
      projectHours,
      internalHours,
      totalHours: projectHours + internalHours,
    };
  }, [projectList, internalList]);

  const chartData = useMemo(() => {
    if (projectList.length === 0) return [];
    if (projectList.length <= TOP_PROJECTS_IN_CHART) {
      return projectList.map((p) => ({
        name: p.name,
        code: p.code,
        hours: Number(p.decimal.toFixed(2)),
      }));
    }
    const top = projectList.slice(0, TOP_PROJECTS_IN_CHART);
    const rest = projectList.slice(TOP_PROJECTS_IN_CHART);
    const restHours = rest.reduce((s, p) => s + p.decimal, 0);
    return [
      ...top.map((p) => ({
        name: p.name,
        code: p.code,
        hours: Number(p.decimal.toFixed(2)),
      })),
      {
        name: `Weitere (${rest.length})`,
        code: "",
        hours: Number(restHours.toFixed(2)),
      },
    ];
  }, [projectList]);

  const chartColors = useMemo(
    () => ({
      bar: getComputedThemeValue("--accent-color") || "#8f84b6",
      grid: getComputedThemeValue("--text-bg-color") || "#e5e7eb",
      text: getComputedThemeValue("--text-color") || "#374151",
    }),
    // Re-read CSS vars when theme flips
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentTheme]
  );

  const formatPercent = (hours, total) =>
    total > 0 ? `${((hours / total) * 100).toFixed(1)}%` : "—";

  // ----- Render ---------------------------------------------------------

  if (memberLoading) {
    return <div className="loading">Person wird geladen...</div>;
  }

  if (error && !member) {
    return (
      <div className="person-auswertung">
        <div className="person-auswertung-error">{error}</div>
        <button className="btn btn-secondary" onClick={() => navigate("/main-team")}>
          Zurück zum Team
        </button>
      </div>
    );
  }

  if (!member) return null;

  const hasHolidayData = gleitzeitInfo && parseFloat(gleitzeitInfo.holidayEntitlement) > 0;
  const showDataSections = !loading && gleitzeitInfo;

  return (
    <div className="person-auswertung">
      <div className="person-auswertung-header">
        <div className="person-header-info">
          {member.bildUrls?.thumbnail && (
            <img
              src={member.bildUrls.medium || member.bildUrls.thumbnail}
              alt={member.name}
              className="person-avatar"
            />
          )}
          <div>
            {canViewAnyone && sortedMembers.length > 1 ? (
              <div className="person-name-switcher" ref={personMenuRef}>
                <h1
                  className="person-name-trigger"
                  role="button"
                  tabIndex={0}
                  aria-haspopup="listbox"
                  aria-expanded={personMenuOpen}
                  onClick={() => setPersonMenuOpen((o) => !o)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setPersonMenuOpen((o) => !o);
                    }
                  }}
                >
                  {member.name}
                  <span className="person-name-caret" aria-hidden="true">▾</span>
                </h1>
                {personMenuOpen && (
                  <ul className="person-name-menu" role="listbox">
                    {sortedMembers.map((m) => (
                      <li key={m.teamId}>
                        <button
                          type="button"
                          className={`person-name-option${m.teamId === member.teamId ? " is-active" : ""}`}
                          role="option"
                          aria-selected={m.teamId === member.teamId}
                          onClick={() => {
                            setPersonMenuOpen(false);
                            if (m.teamId !== member.teamId) {
                              navigate(`/team/${m.teamId}/auswertung`);
                            }
                          }}
                        >
                          {m.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <h1>{member.name}</h1>
            )}
            <div className="person-meta">
              {member.kategorie && <span className="category-badge-detail">{member.kategorie}</span>}
              {member.nummer && <span>{member.nummer}</span>}
              {member.pensum != null && <span>{member.pensum}%</span>}
            </div>
          </div>
        </div>

        <div className="person-header-actions">
          <label className="year-selector">
            <span>Jahr</span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
            >
              {AVAILABLE_YEARS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate("/main-team")}>
            Zurück
          </button>
        </div>
      </div>

      {error && member && <div className="person-auswertung-error">{error}</div>}

      {loading ? (
        <div className="loading">Auswertung wird geladen...</div>
      ) : gleitzeitInfo ? (
        <div className="kpi-grid">
          <div className="kpi-card">
            <h2 className="kpi-title">Arbeitszeit {selectedYear}</h2>
            <div className="kpi-rows">
              <div className="kpi-row">
                <span className="kpi-label">Soll (Jahr)</span>
                <span className="kpi-value">{gleitzeitInfo.personalJahresarbeitszeit} h</span>
              </div>
              <div className="kpi-row">
                <span className="kpi-label">Soll Aktuell</span>
                <span className="kpi-value">{gleitzeitInfo.expectedHours} h</span>
              </div>
              <div className="kpi-row">
                <span className="kpi-label">Ist Aktuell</span>
                <span className="kpi-value">{totalHoursWorked.toFixed(1)} h</span>
              </div>
              <div className="kpi-row kpi-row-main">
                <span className="kpi-label">Gleitzeit-Delta</span>
                <span className={`kpi-value ${gleitzeitValue < 0 ? "negative" : "positive"}`}>
                  {gleitzeitValue >= 0 ? "+" : ""}
                  {gleitzeitInfo.gleitzeit} h
                </span>
              </div>
            </div>
            {gleitzeitInfo.latestCorrection && (
              <div className="kpi-footnote">
                Soll/Ist Aktuell berechnet seit letzter Korrektur ({gleitzeitInfo.latestCorrection.date})
              </div>
            )}
          </div>

          {hasHolidayData && (
            <div className="kpi-card">
              <h2 className="kpi-title">Ferien {selectedYear}</h2>
              <div className="kpi-rows">
                <div className="kpi-row">
                  <span className="kpi-label">Anspruch</span>
                  <span className="kpi-value">{gleitzeitInfo.holidayEntitlement} Tage</span>
                </div>
                <div className="kpi-row">
                  <span className="kpi-label">Genommen</span>
                  <span className="kpi-value">{gleitzeitInfo.holidaysTaken} Tage</span>
                </div>
                <div className="kpi-row">
                  <span className="kpi-label">Geplant</span>
                  <span className="kpi-value">{formatDays(holidaysPlanned)} Tage</span>
                </div>
                <div className="kpi-row kpi-row-main">
                  <span className="kpi-label">Verbleibend</span>
                  <span
                    className={`kpi-value ${
                      parseFloat(gleitzeitInfo.holidaysRemaining) < 0 ? "negative" : "positive"
                    }`}
                  >
                    {gleitzeitInfo.holidaysRemaining} Tage
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        !error && (
          <div className="no-data-message">Keine Daten für {selectedYear} verfügbar.</div>
        )
      )}

      {showDataSections && (
        <>
          <section className="detail-section">
            <h2 className="detail-section-title">Je Kalenderwoche</h2>
            {weeklyData.length === 0 ? (
              <div className="no-data-message">Keine Einträge für {selectedYear}.</div>
            ) : (
              <div className="detail-table-wrapper">
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>KW</th>
                      <th>Datumsbereich</th>
                      <th className="num-cell">Ist</th>
                      <th className="num-cell">Soll</th>
                      <th className="num-cell">Delta</th>
                      <th>Ferientage</th>
                      <th className="num-cell">Überzeit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyData.map((w) => (
                      <tr key={w.key} className={w.overtime > 0 ? "row-overtime" : ""}>
                        <td>{w.label}</td>
                        <td>{w.dateRange}</td>
                        <td className="num-cell">{formatHours(w.istHours)} h</td>
                        <td className="num-cell">{formatHours(w.sollHours)} h</td>
                        <td className={`num-cell ${w.delta < 0 ? "negative" : "positive"}`}>
                          {w.delta >= 0 ? "+" : ""}
                          {formatHours(w.delta)} h
                        </td>
                        <td>
                          {w.ferienTaken > 0 && <span>{w.ferienTaken} Tage genommen</span>}
                          {w.ferienTaken > 0 && w.ferienPlanned > 0 && <span>, </span>}
                          {w.ferienPlanned > 0 && (
                            <span className="ferien-planned">{w.ferienPlanned} Tage geplant</span>
                          )}
                          {w.ferienTaken === 0 && w.ferienPlanned === 0 && <span className="muted">—</span>}
                        </td>
                        <td className="num-cell">
                          {w.overtime > 0 ? (
                            <span className="overtime-value">{formatHours(w.overtime)} h</span>
                          ) : (
                            ""
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2}>Total</td>
                      <td className="num-cell">{formatHours(weeklyTotals.ist)} h</td>
                      <td className="num-cell">{formatHours(weeklyTotals.soll)} h</td>
                      <td
                        className={`num-cell ${
                          weeklyTotals.delta < 0 ? "negative" : "positive"
                        }`}
                      >
                        {weeklyTotals.delta >= 0 ? "+" : ""}
                        {formatHours(weeklyTotals.delta)} h
                      </td>
                      <td>
                        {weeklyTotals.ferienTaken} genommen
                        {weeklyTotals.ferienPlanned > 0 && (
                          <>
                            {", "}
                            <span className="ferien-planned">
                              {weeklyTotals.ferienPlanned} geplant
                            </span>
                          </>
                        )}
                      </td>
                      <td className="num-cell">
                        {weeklyTotals.overtime > 0
                          ? `${formatHours(weeklyTotals.overtime)} h`
                          : ""}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>

          <section className="detail-section">
            <h2 className="detail-section-title">Je Quartal</h2>
            <div className="detail-table-wrapper">
              <table className="detail-table">
                <thead>
                  <tr>
                    <th>Quartal</th>
                    <th className="num-cell">Ist</th>
                    <th className="num-cell">Soll</th>
                    <th className="num-cell">Delta</th>
                    <th className="num-cell">Ferien genommen</th>
                  </tr>
                </thead>
                <tbody>
                  {quarterlyData.map((q) => (
                    <tr key={q.key}>
                      <td>{q.label}</td>
                      <td className="num-cell">{formatHours(q.istHours)} h</td>
                      <td className="num-cell">{formatHours(q.sollHours)} h</td>
                      <td className={`num-cell ${q.delta < 0 ? "negative" : "positive"}`}>
                        {q.delta >= 0 ? "+" : ""}
                        {formatHours(q.delta)} h
                      </td>
                      <td className="num-cell">{q.ferienTaken}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td className="num-cell">{formatHours(quarterlyTotals.ist)} h</td>
                    <td className="num-cell">{formatHours(quarterlyTotals.soll)} h</td>
                    <td
                      className={`num-cell ${
                        quarterlyTotals.delta < 0 ? "negative" : "positive"
                      }`}
                    >
                      {quarterlyTotals.delta >= 0 ? "+" : ""}
                      {formatHours(quarterlyTotals.delta)} h
                    </td>
                    <td className="num-cell">{quarterlyTotals.ferienTaken}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {canSeeKiga && (
            <section className="detail-section kiga-section">
              <h2 className="detail-section-title">Überzeit-Übersicht (ArG)</h2>

              <div className="kiga-summary">
                <div className="kiga-stats">
                  <div className="kiga-stat">
                    <span className="kiga-stat-label">Kumulierte Überzeit</span>
                    <span className="kiga-stat-value">{formatHours(kigaAccumulated)} h</span>
                  </div>
                  <div className="kiga-stat">
                    <span className="kiga-stat-label">Jahreslimit (ArG)</span>
                    <span className="kiga-stat-value">{KIGA_JAHRES_LIMIT} h</span>
                  </div>
                  <div className="kiga-stat">
                    <span className="kiga-stat-label">Verbleibend</span>
                    <span
                      className={`kiga-stat-value ${
                        kigaStatus === "error" ? "negative" : "positive"
                      }`}
                    >
                      {formatHours(kigaRemaining)} h
                    </span>
                  </div>
                </div>

                <div className="kiga-progress">
                  <div className={`kiga-progress-bar status-${kigaStatus}`}>
                    <div
                      className="kiga-progress-fill"
                      style={{ width: `${Math.min(100, kigaPercent)}%` }}
                    />
                  </div>
                  <div className="kiga-progress-caption">
                    {kigaPercent.toFixed(0)}% des Jahreslimits
                  </div>
                </div>

                <p className="kiga-footnote">
                  ArG-Wochenlimit Büro/Verwaltung: {ArG_WEEK_LIMIT}h. Stunden darüber werden als
                  Überzeit kumuliert (Jahreslimit {KIGA_JAHRES_LIMIT}h).
                </p>
              </div>

              {overtimeWeeks.length > 0 ? (
                <div className="detail-table-wrapper">
                  <table className="detail-table">
                    <thead>
                      <tr>
                        <th>KW</th>
                        <th>Datumsbereich</th>
                        <th className="num-cell">Ist</th>
                        <th className="num-cell">Überzeit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overtimeWeeks.map((w) => (
                        <tr key={w.key} className="row-overtime">
                          <td>{w.label}</td>
                          <td>{w.dateRange}</td>
                          <td className="num-cell">{formatHours(w.istHours)} h</td>
                          <td className="num-cell overtime-value">
                            {formatHours(w.overtime)} h
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-data-message">Keine Überzeit in {selectedYear}.</div>
              )}
            </section>
          )}

          <section className="detail-section">
            <h2 className="detail-section-title">Nach Projekt</h2>

            {projectTotals.totalHours === 0 ? (
              <div className="no-data-message">Keine Einträge für {selectedYear}.</div>
            ) : (
              <>
                {chartData.length > 0 && (
                  <div
                    className="project-chart-container"
                    style={{ height: Math.max(220, chartData.length * 34 + 40) }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        layout="vertical"
                        data={chartData}
                        margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                        <XAxis
                          type="number"
                          stroke={chartColors.text}
                          tickFormatter={(v) => `${v}h`}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={160}
                          stroke={chartColors.text}
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip
                          formatter={(value) => [`${Number(value).toFixed(1)} h`, "Stunden"]}
                        />
                        <Bar dataKey="hours" fill={chartColors.bar} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {projectList.length > 0 && (
                  <div className="project-group">
                    <h3 className="project-group-title">Projekte</h3>
                    <div className="detail-table-wrapper">
                      <table className="detail-table project-table">
                        <thead>
                          <tr>
                            <th>Projekt</th>
                            <th>Code</th>
                            <th className="num-cell">Einträge</th>
                            <th className="num-cell">Stunden</th>
                            <th className="num-cell">Anteil</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projectList.map((p) => (
                            <tr key={`p-${p.code}`}>
                              <td>
                                <span className="project-dot project-dot-project" />
                                {p.name}
                              </td>
                              <td className="project-code-cell">{p.code}</td>
                              <td className="num-cell">{p.count}</td>
                              <td className="num-cell">{p.decimal.toFixed(1)} h</td>
                              <td className="num-cell">
                                {formatPercent(p.decimal, projectTotals.totalHours)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {internalList.length > 0 && (
                  <div className="project-group">
                    <h3 className="project-group-title">Intern / Sonstiges</h3>
                    <div className="detail-table-wrapper">
                      <table className="detail-table project-table">
                        <thead>
                          <tr>
                            <th>Tätigkeit</th>
                            <th></th>
                            <th className="num-cell">Einträge</th>
                            <th className="num-cell">Stunden</th>
                            <th className="num-cell">Anteil</th>
                          </tr>
                        </thead>
                        <tbody>
                          {internalList.map((p) => (
                            <tr key={`i-${p.code}`}>
                              <td>
                                <span className="project-dot project-dot-internal" />
                                {p.name}
                              </td>
                              <td></td>
                              <td className="num-cell">{p.count}</td>
                              <td className="num-cell">{p.decimal.toFixed(1)} h</td>
                              <td className="num-cell">
                                {formatPercent(p.decimal, projectTotals.totalHours)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="project-totals">
                  <span className="project-totals-label">Gesamtsumme</span>
                  <span className="project-totals-value">
                    {projectTotals.totalHours.toFixed(1)} h
                  </span>
                  <span className="project-totals-breakdown">
                    (Projekte {projectTotals.projectHours.toFixed(1)} h · Intern{" "}
                    {projectTotals.internalHours.toFixed(1)} h)
                  </span>
                </div>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default TeamPersonAuswertung;
